import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES } from "./_shared.js";

/**
 * `MEMBER-STATUS-LINK` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/members` (ADR-0009 bất biến 5).
 * US-AIV2-0110 S10 (`@UC0259`): trước trỏ trang chủ nhóm `/groups/:groupId`; nay tab "Thành viên" — màn chi tiết thành viên cần `memberId` mà engine chưa điền được.
 * Cửa mobile trang này chịu trách nhiệm (P5.3 kênh NHẮC riêng tư).
 *
 * 🔴🔴 **GIỮ `redirect` — QĐ PO 22/09/2026 (`US-AIV2-0059`). Đọc trước khi "nâng cho đủ trang chat".**
 *
 * Câu cũ ở đây hứa *"trang chat là task 0059 — khi lên, hai cửa chuyển sang nó"*. Khi làm tới, đo ra
 * một lý lẽ **ngược chiều** và PO chọn giữ nguyên:
 *
 *  · `MemberStatusLinkService.createOrReuse` **CỐ Ý** tách thu-hồi và tạo-mới thành **HAI BƯỚC GHI
 *    RIÊNG**, không cùng transaction — docblock của nó ghi lý do: *"để nếu tạo lỗi thì ít nhất link cũ
 *    đã bị thu hồi, không kẹt ở trạng thái 'vừa cũ vừa mới' mập mờ hơn là 'chỉ còn link mới hỏng'"*.
 *    Nhét seam tx của phiếu vào (như 0043 · 0055 · 0058 đã làm cho ba cửa tiền) sẽ **phá chính lý lẽ
 *    đó** — hai bước lại chung một tx.
 *  · Và AI-INV-7 **không đòi** ở đây: cửa này **0 BÚT TOÁN**, nó tạo một token chứ không ghi đồng nào.
 *
 *  · Khác `SHARE-TRANSPARENCY` (giữ `draft` vì redirect sẽ là ngõ cụt): ở đây **`apps/mobile` ĐÃ có
 *    màn** cho cả hai cửa, nên redirect dẫn tới một màn **có thật** — không vi phạm §4.
 *
 * 🔁 **Xem lại khi:** service tách được bước tạo-mới thành một hàm nhận seam mà **không** gộp lại hai
 * bước; hoặc PO đổi ưu tiên và chấp nhận rủi ro "phiếu rollback mà link đã sống".
 */
export const MEMBER_STATUS_LINK = {
  id: "MEMBER-STATUS-LINK",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: [],
  slots: [],
  llm: {
    description: "Xem hoặc thu hồi LINK GẮN SUẤT đã gửi cho một thành viên (link để họ nhận đúng suất của mình). KHÔNG phải link mời người mới vào nhóm.",
    examples: [
      "gửi link tình trạng đóng cho một người",
      "gui link tinh trang dong cho mot nguoi",
      "tạo link riêng cho phụ huynh",
      "thu hồi link đã gửi",
      "link riêng",
      // ——— 0105: thêm cho đủ 40 ———
      "tạo link xem tình trạng đóng cho một thành viên",
      "tao link xem tinh trang dong cho mot thanh vien",
      "gửi riêng cho phụ huynh đường link xem khoản của họ",
      "gui rieng cho phu huynh duong link xem khoan cua ho",
      "lấy link cá nhân của một bạn trong nhóm",
      "lay link ca nhan cua mot ban trong nhom",
      "thu hồi link gắn suất đã gửi cho Lan",
      "thu hoi link gan suat da gui cho lan",
      "huỷ link riêng của một người",
      "huy link rieng cua mot nguoi",
      "link này để người đó nhận đúng suất của mình",
      "link nay de nguoi do nhan dung suat cua minh",
      "làm link riêng cho từng phụ huynh",
      "lam link rieng cho tung phu huynh",
      "xem link gắn suất đã phát chưa",
      "xem link gan suat da phat chua",
      "gửi lại link tình trạng cho một bạn",
      "gui lai link tinh trang cho mot ban",
      "tạo lại link cá nhân mới cho thành viên",
      "tao lai link ca nhan moi cho thanh vien",
      "link tình trạng đóng của một người",
      "link tinh trang dong cua mot nguoi",
      "gỡ link riêng của bạn Minh đi",
      "go link rieng cua ban minh di",
      "cấp link xem khoản đóng cho một suất",
      "cap link xem khoan dong cho mot suat",
      "link riêng còn hiệu lực không",
      "link rieng con hieu luc khong",
      "làm sao gửi link tình trạng cho một người?",
      "lam sao gui link tinh trang cho mot nguoi",
      "phát link gắn suất",
      "phat link gan suat",
      "link xem khoản riêng của phụ huynh",
      "tạo link gắn suất cho một thành viên chưa có tài khoản",
      "tao link gan suat cho mot thanh vien chua co tai khoan",
      "xem lại link riêng đã cấp cho bạn đó",
      "xem lai link rieng da cap cho ban do",
    ],
  },
  screen: "/groups/:groupId/members",
  screenShows: "member_status_link",
  endpoints: [
    "POST /groups/{groupId}/members/{memberId}/status-link",
    "DELETE /groups/{groupId}/members/{memberId}/status-link",
  ],
  facts: [],
  templates: {
    redirect: "Mình mở trang của người đó nhé. Link này chỉ cho đúng người đó xem — đừng đăng vào nhóm chung.",
  },
  journey: { after: ["READ-PEOPLE-MINE"] },
  help: { title: "Link tình trạng riêng", tryNow: "gửi link tình trạng đóng cho một người" },
} satisfies AiCapabilityPage;
